import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useConfirmationDialogue } from '../../providers/ConfirmationDialogue.provider';
import { usePendingData } from '../../providers/PendingData.provider';

import { useBillingPackages } from '../../providers/BillingPackages.provider';
import type { CustomPackageResponseValue } from '../useApi/usePostCustomPackage';

const usePostCustomPackageDialogue = ({
  success,
  errorMessage,
  isApproved,
}: CustomPackageResponseValue): { destination?: string; navigate: () => void; showDialogue: () => void } => {
  const history = useHistory();
  const [destination, setDestination] = useState<string>();

  const { refreshBillingPackages } = useBillingPackages();
  const { provideProps, showDialogue } = useConfirmationDialogue();
  const { reset } = usePendingData();

  const navigate = () => {
    if (destination) history.push(destination);
  };

  useEffect(() => {
    const handlers = {
      success: () =>
        provideProps({
          title: 'Success! Your custom billing package has been created.',
          status: 'success',
          subTitle: isApproved
            ? 'Your billing package has been approved and is ready for use.'
            : 'Your billing package is pending approval',
          confirm: {
            label: 'Back to Billing Packages',
            action: () => {
              reset();
              refreshBillingPackages();
              setDestination('/billing-packages');
            },
          },
          cancel: {
            label: 'Stay Here',
            action: () => {
              reset();
              refreshBillingPackages();
            },
          },
        }),
      errorMessage: () => {
        provideProps({
          title: 'Error! Your custom billing package has not been created.',
          subTitle: errorMessage,
          status: 'error',
          confirm: {
            label: 'Back to Billing Packages',
            action: () => {
              reset();
              setDestination('/billing-packages');
            },
          },
          cancel: {
            label: 'Stay Here and Try Again',
            action: () => {},
          },
        });
      },
    };

    if (success) return handlers.success();

    return handlers.errorMessage();
  }, [success, errorMessage, isApproved]);

  return { destination, navigate, showDialogue };
};

export default usePostCustomPackageDialogue;
